import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import AdminService from "../../../api/services/adminService";
import "../Home/Homepage.css";
import "./TradersListPage.css";

const KYC_LABELS = { 0: "Not Verified", 1: "Pending", 2: "Verified", 3: "Verified" };
const STATUS_LABELS = { 0: "Inactive", 1: "Active" };

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso || "—";
  }
};

const TradersListPage = () => {
  const navigate = useNavigate();
  const [traders, setTraders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusUpdating, setStatusUpdating] = useState(null);
  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [kycFilter, setKycFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });

  const fetchTraders = useCallback(async () => {
    setLoading(true);
    setError("");
    const params = {
      page: pagination.page,
      limit: pagination.limit,
      ...(searchDebounced.trim() && { search: searchDebounced.trim() }),
      ...(kycFilter !== "" && { kyc_status: kycFilter }),
      ...(statusFilter !== "" && { status: statusFilter }),
    };
    const res = await AdminService.getUsers(params);
    setLoading(false);
    if (res?.success) {
      setTraders(res.data || []);
      const pag = res.pagination || {};
      setPagination((prev) => ({
        ...prev,
        total: pag.total ?? prev.total,
        totalPages: pag.total_pages ?? pag.totalPages ?? prev.totalPages,
      }));
    } else {
      setError(res?.message || "Failed to load traders.");
      setTraders([]);
    }
  }, [pagination.page, pagination.limit, searchDebounced, kycFilter, statusFilter]);

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [searchDebounced, kycFilter, statusFilter]);

  useEffect(() => {
    fetchTraders();
  }, [fetchTraders]);

  const handleResetFilters = () => {
    setSearch("");
    setSearchDebounced("");
    setKycFilter("");
    setStatusFilter("");
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pagination.totalPages) return;
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleLimitChange = (e) => {
    setPagination((prev) => ({
      ...prev,
      limit: Number(e.target.value) || 20,
      page: 1,
    }));
  };

  const rows = traders.map((t) => {
    const firstName = t.firstName ?? t.first_name ?? "";
    const lastName = t.lastName ?? t.last_name ?? "";
    const emailDisplay = t.emailId ?? t.email_masked ?? "—";
    const mobileDisplay = t.mobile_masked ?? (t.mobileNumber
      ? `${(t.country_code ?? "").toString().trim()} ${t.mobileNumber}`.trim()
      : (t.mobileNumber ?? "—"));
    return {
      ...t,
      emailId: emailDisplay,
      mobileNumber: mobileDisplay,
      uuid: t.uuid ?? t.uuid_masked ?? "—",
      name: `${firstName} ${lastName}`.trim() || "—",
      kycLabel: KYC_LABELS[t.kycVerified ?? t.kyc_verified] ?? "—",
      statusLabel: STATUS_LABELS[t.status] ?? (t.status === 1 ? "Active" : t.status === 0 ? "Inactive" : String(t.status ?? "—")),
      createdLabel: formatDate(t.createdAt ?? t.signup_date),
    };
  });

  const columns = [
    { key: "emailId", label: "Email" },
    { key: "mobileNumber", label: "Mobile" },
    { key: "name", label: "Name" },
    { key: "uuid", label: "UUID" },
    { key: "statusLabel", label: "Status" },
    { key: "kycLabel", label: "KYC" },
    { key: "referral_code", label: "Referral Code" },
    { key: "createdLabel", label: "Created" },
    { key: "actions", label: "Actions" },
  ];

  const handleBlockToggle = async (trader) => {
    if (!trader._id || statusUpdating) return;
    const newStatus = trader.status === 1 || trader.status === "Active" ? "Inactive" : "Active";
    setStatusUpdating(trader._id);
    const res = await AdminService.updateUserStatus(trader._id, newStatus);
    setStatusUpdating(null);
    if (res?.success) {
      setTraders((prev) =>
        prev.map((t) =>
          t._id === trader._id
            ? { ...t, status: newStatus === "Active" ? 1 : 0 }
            : t
        )
      );
    }
  };


  return (
    <main className="dashboard-main dashboard-subpage">
      <section className="dashboard-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Traders List</span>
          </p>
          <h1 className="dashboard-hero__title">Traders List</h1>
          <p className="dashboard-hero__subtitle">
            View and manage registered traders
          </p>
        </div>
        <div className="dashboard-hero__icon">
          <i className="fas fa-user-tie" />
        </div>
      </section>

      <div className="dashboard-body">
        <section className="traders-section">
          <div className="traders-section__header">
            <div className="traders-section__title-wrap">
              <span className="traders-section__icon"><i className="fas fa-users" /></span>
              <h2 className="traders-section__title">Traders List</h2>
            </div>
            <div className="traders-section__stats">
              <span className="traders-stat">
                <strong>{pagination.total}</strong> Total
              </span>

            </div>
          </div>

          <form onSubmit={(e) => e.preventDefault()} className="traders-filters">
            <div className="traders-filters__row">
              <input
                type="text"
                placeholder="Search email, UUID, mobile, name, referral..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="traders-filter-search"
              />
              <select
                value={kycFilter}
                onChange={(e) => setKycFilter(e.target.value)}
                className="traders-filter-select"
              >
                <option value="">All KYC</option>
                <option value="0">Not Verified</option>
                <option value="1">Pending</option>
                <option value="2">Verified (Complete KYC)</option>
              </select>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="traders-filter-select"
              >
                <option value="">All Status</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
              <select
                value={pagination.limit}
                onChange={handleLimitChange}
                className="traders-filter-select"
              >
                <option value={10}>10 / page</option>
                <option value={20}>20 / page</option>
                <option value={50}>50 / page</option>
                <option value={100}>100 / page</option>
              </select>
              <button type="button" className="traders-filter-btn" onClick={handleResetFilters}>
                <i className="fas fa-undo" /> Reset
              </button>
            </div>
          </form>

          {error && (
            <div className="traders-error">
              <i className="fas fa-exclamation-circle" /> {error}
            </div>
          )}

          <div className="traders-list-content">
            <div className="traders-table-wrap">
              <table className="traders-table">
                <thead>
                  <tr>
                    {columns.map((col) => (
                      <th key={col.key}>{col.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading && traders.length === 0 ? (
                    <tr>
                      <td colSpan={columns.length} className="traders-loading">
                        <i className="fas fa-spinner fa-spin" /> Loading traders…
                      </td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={columns.length} className="traders-empty">
                        No traders found.
                      </td>
                    </tr>
                  ) : (
                    rows.map((row, i) => (
                      <tr key={row._id || i}>
                        {columns.map((col) => {
                          if (col.key === "actions") {
                            return (
                              <td key={col.key} className="traders-table__actions">
                                <button
                                  type="button"
                                  className="btn-icon btn-icon--view"
                                  onClick={() => navigate(`/dashboard/trader/${row._id}`, { state: { trader: row } })}
                                  title="View details"
                                >
                                  <i className="fas fa-eye" />
                                </button>
                                <button
                                  type="button"
                                  className={`btn-icon ${row.status === 1 || row.status === "Active" ? "btn-icon--unblock" : "btn-icon--block"}`}
                                  onClick={() => handleBlockToggle(row)}
                                  disabled={statusUpdating === row._id}
                                  title={row.status === 1 || row.status === "Active" ? "Block" : "Unblock"}
                                >
                                  {statusUpdating === row._id ? (
                                    <i className="fas fa-spinner fa-spin" />
                                  ) : (
                                    <i
                                      className={
                                        row.status === 1 || row.status === "Active"
                                          ? "fas fa-lock"
                                          : "fas fa-unlock"
                                      }
                                    />
                                  )}
                                </button>
                              </td>
                            );
                          }
                          if (col.key === "statusLabel") {
                            return (
                              <td key={col.key}>
                                <span
                                  className={`badge badge--${
                                    row.status === 1 || row.status === "Active" ? "active" : "inactive"
                                  }`}
                                >
                                  {row.statusLabel}
                                </span>
                              </td>
                            );
                          }
                          if (col.key === "uuid") {
                            return (
                              <td key={col.key}>
                                <button
                                  type="button"
                                  className="traders-table__uuid-link"
                                  onClick={() => navigate(`/dashboard/trader/${row._id}`, { state: { trader: row } })}
                                  title="View user details"
                                >
                                  {row.uuid}
                                </button>
                              </td>
                            );
                          }
                          return (
                            <td key={col.key}>{row[col.key] ?? "—"}</td>
                          );
                        })}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {pagination.totalPages > 1 && !loading && (
              <div className="traders-pagination">
                <span className="traders-pagination__info">
                  Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
                </span>
                <div className="traders-pagination__buttons">
                  <button
                    type="button"
                    className="traders-pagination__btn"
                    disabled={pagination.page <= 1}
                    onClick={() => handlePageChange(pagination.page - 1)}
                  >
                    <i className="fas fa-chevron-left" /> Prev
                  </button>
                  <button
                    type="button"
                    className="traders-pagination__btn"
                    disabled={pagination.page >= pagination.totalPages}
                    onClick={() => handlePageChange(pagination.page + 1)}
                  >
                    Next <i className="fas fa-chevron-right" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>

    </main>
  );
};

export default TradersListPage;
